import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Container from '../Container';
import AndModal from '../Common/AndModal';
import * as Api from '../../services/api';
import FullWidthLayout from '../../layouts/FullWidthLayout';

import './PublicProfile.css';
import { atom, useAtom } from 'jotai';
import { Profile, ProfileRecord } from '../../types/profile';
import ConnectModalDialog from './ConnectModalDialog';
import ShareableCard from './ShareableCard';
import { BasicProfile } from '../../utils/qr';

export const andTagAtom = atom<string>('');
const profileAtom = atom(
  async (get) => {
    const andTag = get(andTagAtom);

    return Api.getProfile(andTag).catch(() =>
      Api.getTempProfile(andTag).catch(() => generateDefaultProfile(andTag))
    );
  },
  async (get, set, updatedProfile) => {
    const { data } = updatedProfile as ProfileRecord;
    set(profileAtom, data);
  }
);

export const webCardDataAtom = atom<Promise<Profile>>(async (get) => {
  const profile: ProfileRecord = await get(profileAtom);
  return profile.data;
});

export default function RegularPublicProfile({
  andTag,
  modalMode = 'fullscreen',
}: {
  andTag: string;
  modalMode?: 'center' | 'fullscreen';
}) {
  const cleanProfileUrl = parseCurrentUrl();

  // Actions
  const [searchParams, setSearchParams] = useSearchParams();

  // Atoms
  const [profile] = useAtom(webCardDataAtom);

  const basicProfile: BasicProfile = {
    andTag: profile.andTagMain || profile.andHandles[0],
    firstName: profile.firstName,
    profilePicUrl: profile.profilePicUrl,
  };

  // States
  const [documentTitle, setDocumentTitle] = useState('&');
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<string>(searchParams.get('a') || '');

  useEffect(() => {
    if (profile) {
      setDocumentTitle(
        `${profile.firstName} ${profile.lastName} (${andTag}) | The & Company`
      );
    }
  }, [profile, andTag]);
  const handleAction = () => {
    const isUnclaimed = profile.personId === 'unclaimed-id';
    let a = searchParams.get('a');
    console.log('actionQueryParam', a);
    if (!a) {
      a = isUnclaimed ? 'cl' : 'co';
      setSearchParams({ a });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (action) {
      handleAction();
    }
  }, []);

  function onModalCloseHandler() {
    setOpen(false);
    setAction('');
    setSearchParams({});
  }

  return (
    <FullWidthLayout showHeader={false} showTryApp={false}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{documentTitle}</title>
        <link rel="canonical" href={cleanProfileUrl} />
        <meta property="og:url" content={cleanProfileUrl} />
        <meta property="og:title" content={documentTitle} />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content={profile.profilePicUrl!} />
      </Helmet>
      <Container
        styles={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 10,
          paddingRight: 0,
          paddingLeft: 0,
        }}
      >
        <AndModal
          open={open}
          onClose={onModalCloseHandler}
          modalMode={modalMode}
        >
          <ConnectModalDialog
            basicProfile={basicProfile}
            queryParams={searchParams}
          />
        </AndModal>
        <ShareableCard
          andTag={andTag}
          profile={profile}
          handleActionButtonClick={handleAction}
        />
      </Container>
    </FullWidthLayout>
  );
}

// Create a clean url by parsing out all the query params
function parseCurrentUrl() {
  const url = window.location.href;
  const urlParts = url.split('?');
  return urlParts[0];
}

const generateDefaultProfile = async (id) => {
  const cleanId = id.replace('&', '');
  let purpose = '';
  let accounts = {};

  try {
    const results = await Api.generatePurpose(cleanId);
    purpose = results.description;
    accounts = results.accounts;
  } catch {
    purpose = 'Claim this &tag. Connect with the world.';
    accounts = {};
  }

  const defaultProfile = {
    partitionKey: cleanId,
    data: {
      unclaimed: true,
      andHandles: [cleanId],
      andTagMain: cleanId,
      dob: null,
      emailAddress: null,
      firstName: 'is',
      homeAddress: null,
      isDeleted: false,
      isVerified: false,
      lastName: 'available!',
      paymentAccounts: {},
      personalLinks: {},
      personId: 'unclaimed-id',
      phoneNumber: null,
      professionalTitle: '-',
      profilePicUrl: null,
      pronouns: '-',
      purposeStatement: purpose,
      socialAccounts: accounts,
    },
  };
  Api.createTempProfile(defaultProfile);
  return defaultProfile;
};
